/**
 * The methodology an ai-ingest commit leaves on the task, following the rows' own write mode.
 *
 * The editor on the commit rail shows what the task's methodology for this scope will be AFTER the commit:
 *  - Add to existing rows  → the editor starts with the task's current methodology, which the user can edit;
 *                            Generate appends to it.
 *  - Replace all rows      → the editor starts empty: the task's methodology is replaced along with its rows.
 *
 * The backend always APPENDS its own short record on commit (`AiIngestFacade.appendMethodology`): an
 * `<!-- ai-ingest:target=X -->` section with the outcome ("39 rows into …, committed …") and a pointer to the
 * run's full trail. So committing is two steps:
 *   1. commit with an EMPTY methodology summary, so the backend's section holds only that record;
 *   2. save the scope's methodology as (the editor's content + that freshly written section), through the
 *      existing `PATCH /organization-questionnaires/{id}/methodology` — which is exactly what the editor
 *      showed, plus the commit's own audit record.
 *
 * Step 1 alone would leave the old text (Replace) or drop the user's edits (Append); step 2 alone would lose
 * the record. Doing it after a successful commit means a failure there leaves the data committed and the
 * methodology at the backend's appended state — nothing is lost.
 *
 * Standalone (not in the `src/utils` barrel) so it can carry its own vitest.
 */

import { SCOPE_SUB_TYPES, methodologyForSubType, subKeyForTarget } from "../../../utils/methodologySubKeys";

/** The ai-ingest scope key for a target — mirrors AiIngestTarget.getMethodologyScopeKey() in Java. */
const SCOPE_KEY_BY_TARGET = {
  SCOPE_1_STATIONARY: "scope1",
  SCOPE_1_MOBILE: "scope1",
  SCOPE_1_REFRIGERANT: "scope1",
  SCOPE_2_V1_ELECTRICITY: "scope2",
  SCOPE_2_V2_PURCHASED_ELECTRICITY: "scope2",
  SCOPE_2_V2_ONSITE_GENERATION: "scope2",
  SCOPE_3_BUSINESS_TRAVEL: "businessTravel",
  SCOPE_3_EMPLOYEE_COMMUTING: "employeeCommuting",
  SCOPE_3_CAPITAL_GOODS: "capitalGoods",
  SCOPE_3_PURCHASED_GOODS: "purchasedGoodsAndServices",
};

export function methodologyScopeKeyFor(target) {
  return SCOPE_KEY_BY_TARGET[target] ?? null;
}

/**
 * The methodology currently saved on the task for this target's scope. `scope1` falls back to the legacy
 * single `methodology` field, as the task page itself does.
 */
export function currentMethodologyFor(questionnaire, target) {
  const scopeKey = methodologyScopeKeyFor(target);
  if (!questionnaire || !scopeKey) return "";
  const fromMap = questionnaire.methodologyMap?.[scopeKey];
  if (fromMap != null) return fromMap;
  return scopeKey === "scope1" ? questionnaire.methodology ?? "" : "";
}

/**
 * Where this target's methodology is shown and saved on the task: its own per-type key when the scope has
 * types (Scope 1 → `scope1.stationary` etc., see utils/methodologySubKeys), else the scope key. The backend
 * still writes its commit record into the SHARED scope key — read that with currentMethodologyFor.
 */
export function methodologySaveKeyFor(target) {
  return subKeyForTarget(target) ?? methodologyScopeKeyFor(target);
}

/** The methodology the task shows for this target — the per-type tab's content where there is one. */
export function taskMethodologyFor(questionnaire, target) {
  const scopeKey = methodologyScopeKeyFor(target);
  const subKey = subKeyForTarget(target);
  if (!questionnaire || !scopeKey) return "";
  if (!subKey) return currentMethodologyFor(questionnaire, target);
  const types = SCOPE_SUB_TYPES[scopeKey] || [];
  const type = types.find((candidate) => candidate.key === subKey);
  const map = {
    ...(scopeKey === "scope1" && questionnaire.methodologyMap?.scope1 == null && questionnaire.methodology
      ? { scope1: questionnaire.methodology }
      : {}),
    ...(questionnaire.methodologyMap || {}),
  };
  return methodologyForSubType(map, scopeKey, type, types[0]?.key === subKey);
}

/** True when the HTML has visible text (TipTap's empty document is "<p></p>"). */
export function hasText(html) {
  return Boolean(html && html.replace(/<[^>]*>/g, "").trim());
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The backend's section for one target in a saved methodology, markers included — or "" if absent. */
export function sectionForTarget(savedHtml, target) {
  if (!savedHtml || !target) return "";
  const re = new RegExp(
    `<!--\\s*ai-ingest:target=${escapeRe(target)}\\s*-->[\\s\\S]*?<!--\\s*\\/ai-ingest\\s*-->`
  );
  const match = savedHtml.match(re);
  return match ? match[0] : "";
}

/**
 * The scope's methodology after a commit: the editor's content, then the backend's freshly written record
 * for this target. The record goes last so it reads as a footnote to the statement above it.
 */
export function methodologyAfterCommit(editorHtml, savedAfterCommit, target) {
  const record = sectionForTarget(savedAfterCommit, target);
  const body = hasText(editorHtml) ? editorHtml.trim() : "";
  if (body && record) return `${body}\n\n${record}`;
  return body || record;
}

/**
 * Where Generate's draft goes: after whatever is in the editor (Append starts it with the task's current
 * methodology), or on its own when the editor is empty.
 */
export function appendDraft(editorHtml, draftHtml) {
  if (!hasText(draftHtml)) return editorHtml ?? "";
  if (!hasText(editorHtml)) return draftHtml;
  return `${editorHtml.trim()}\n${draftHtml}`;
}
